<script lang="ts">
	import type { SbLink, SbVideo, StripeProductData } from '../types';
	import BtnPlayVideo from '$lib/buttons/v2/btnPlayVideo.svelte';
	import SbRichtext from '../components/sbRichtext.svelte';
	import { getSbUrl } from '$lib/utils/storyblok/url';
	import Button from '$lib/buttons/v3/button.svelte';
	import { slideIn } from '$lib/actions/slideIn';
	import PageGrid16 from '../components/pageGrid16.svelte';
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import { editable } from '../editable';
	import Blok from '../blok.svelte';
	import { device } from '$lib/state/device.svelte';
	import { checkoutSession } from '$lib/state/checkoutSession.svelte';
	import { shoppingCart } from '$lib/state/shoppingCart.svelte';

	interface Props {
		content: ISbStoryData<{
			asset: Array<ISbStoryData['content']>;
			asset_desktop: Array<ISbStoryData['content']>;
			overlay: boolean;
			alignment: 'center' | 'left' | 'bottom-left';
			mobile_bottom_aligned: boolean;
			headline: ISbRichtext;
			subline?: ISbRichtext;
			cta_text?: string;
			cta_url: SbLink;
			cta_color: 'green-3' | 'green-5' | 'white';
			show_circle_outline: boolean;
			fullscreen_video: Array<ISbStoryData<SbVideo>['content']>;
			fullscreen_video_desktop: Array<ISbStoryData<SbVideo>['content']>;
			bundle_id?: string;
			product?: StripeProductData;
		}>['content'];
	}

	let { content }: Props = $props();

	function getBtnTheme(color: typeof content.cta_color): string | undefined {
		switch (color) {
			case 'white':
				return 'btn-stone';
			case 'green-5':
				return 'btn-lime';
			case 'green-3':
			default:
				return undefined;
		}
	}

	let btnTheme = $derived(getBtnTheme(content.cta_color));

	let asset = $derived(
		device.data.isDesktop ? content.asset_desktop?.[0] || content.asset?.[0] : content.asset?.[0]
	);

	let fullscreenVideo = $derived(
		device.data.isDesktop
			? content.fullscreen_video_desktop?.[0] || content.fullscreen_video?.[0]
			: content.fullscreen_video?.[0]
	);
</script>

<section
	use:editable={content}
	id="hero"
	data-testid="hero-section"
	class="relative flex w-full flex-col lg:justify-center"
	class:justify-center={content.alignment === 'center'}
	class:justify-end={content.alignment === 'left'}
	class:pb-10={content.alignment === 'left'}
>
	{#if asset}
		<div class="absolute inset-0"><Blok content={asset} /></div>
	{/if}

	{#if content.overlay}
		<div class="absolute inset-0 bg-black/20"></div>
	{/if}

	<PageGrid16 margin class="relative h-full" gridClass="h-full">
		<div
			class="relative col-span-full flex flex-col items-center max-lg:items-start
				{content.alignment === 'bottom-left'
				? 'lg:col-start-1 lg:col-end-9 lg:justify-end lg:pb-25'
				: 'lg:col-start-2 lg:col-end-[16]'}"
			class:lg:px-22={content.alignment === 'left'}
			class:justify-center={!content.mobile_bottom_aligned}
			class:lg:justify-center={content.mobile_bottom_aligned && content.alignment !== 'bottom-left'}
			class:max-lg:h-full={content.mobile_bottom_aligned}
			class:max-lg:justify-end={content.mobile_bottom_aligned}
			class:max-lg:pb-12={content.mobile_bottom_aligned}
		>
			<div
				class="flex flex-col justify-center gap-y-4 max-lg:items-start"
				class:aspect-square={content.show_circle_outline}
				class:max-w-[90%]={content.show_circle_outline}
				class:lg:max-w-[464px]={content.show_circle_outline}
				class:border-2={content.show_circle_outline}
				class:border-white={content.show_circle_outline}
				class:rounded-full={content.show_circle_outline}
				class:items-center={!['bottom-left', 'left'].includes(content.alignment)}
				class:items-start={['bottom-left', 'left'].includes(content.alignment)}
			>
				<div
					class="sb-rt-hero text-balance text-[52px]/[108%] font-semibold tracking-[-2%] text-white opacity-[0.01] lg:text-[80px]/[100%]"
					class:lg:text-center={content.alignment === 'center'}
					use:slideIn={{ duration: 480, threshold: 0 }}
				>
					<SbRichtext text={content.headline} />
				</div>

				{#if content.subline}
					<div
						use:slideIn={{ duration: 480, delay: 80, threshold: 0 }}
						class="sb-rt-hero text-balance text-[20px]/[140%] tracking-[-1%] text-white opacity-[0.01] empty:hidden lg:text-[24px]/[140%]"
						class:lg:text-center={content.alignment === 'center'}
						class:w-[95%]={content.show_circle_outline}
					>
						<SbRichtext text={content.subline} />
					</div>
				{/if}
			</div>

			{#if fullscreenVideo || (content.cta_text && content.cta_text.length > 0)}
				<div
					use:slideIn={{ duration: 480, delay: 120, threshold: 0 }}
					class="z-20 mt-8 flex w-full gap-x-3 opacity-[0.01] lg:mt-12"
					class:lg:justify-center={content.alignment === 'center'}
				>
					{#if content.cta_text && content.cta_text.length > 0}
						<Button
							class={[btnTheme, 'btn-large'].filter(Boolean).join(' ')}
							{...content.product
								? {
										onclick: () => {
											checkoutSession.select(content.product!, {
												productBundle: content.bundle_id
											});
											shoppingCart.open();
										}
									}
								: {
										href: getSbUrl(content.cta_url)
									}}>{content.cta_text}</Button
						>
					{/if}
					{#if fullscreenVideo}
						<BtnPlayVideo playbackId={fullscreenVideo.playback_id} />
					{/if}
				</div>
			{/if}
		</div>
	</PageGrid16>
</section>

<style lang="scss">
	section {
		height: calc(100svh - var(--banner-height));
		transition-property: padding-top, height;
		transition-duration: 0.2s;
		padding-top: var(--banner-height);
	}

	div.sb-rt-hero {
		:global {
			span.font-huge {
				font-weight: theme('fontWeight.normal');
				font-size: 64px;
				line-height: 57.88px;
				letter-spacing: -2%;

				@media (min-width: 1024px) {
					font-size: 82px;
					line-height: 74.16px;
					letter-spacing: -2%;
				}
			}

			span.font-serif-huge {
				font-weight: theme('fontWeight.normal');
				font-family: Catalogue;
				font-size: 64px;
				line-height: 57.88px;
				letter-spacing: -5%;

				@media (min-width: 1024px) {
					font-size: 82px;
					line-height: 74.16px;
					letter-spacing: -5%;
				}
			}

			span.font-bigger {
				font-size: 24px;
				line-height: 26.88px;
			}
		}
	}
</style>
